import '@uppy/core/dist/style.min.css';
import '@uppy/drag-drop/dist/style.min.css';
import '@uppy/status-bar/dist/style.min.css';

import { createUppy, FileUploadConfig } from 'app/core/hooks/createUppy';
import clsx from 'clsx';
import { uniqBy } from 'lodash-es';
import { useEffect, useState } from 'react';

import { Box, useTheme } from '@mui/material';
import { DragDrop, StatusBar } from '@uppy/react';

import { FileInfo } from '../multi-file-uploader2/MultiFileUploader2';
import { getStyles } from './file-upload-styles';

export interface FileUploadProps {
  config: FileUploadConfig;
  isLoading?: boolean;
  id: string;
  idToRemove?: string;
  idToRetry?: string;
  initialState?: FileInfo[];
  dragDropVisible?: boolean;
  isDisabled?: boolean;
  rawFilesToUpload?: File[];
  reset?: number;
}

function prepareState(data: FileInfo[], limit: number): FileInfo[] {
  const uniqueData = uniqBy(data, 'name');
  return uniqueData.slice(0, limit);
}

export default function FileUpload(props: FileUploadProps) {
  const {
    config,
    id,
    idToRemove,
    idToRetry,
    initialState,
    dragDropVisible = true,
    isDisabled = false,
    rawFilesToUpload,
    reset = 0,
  } = props;

  const filesDropNote = config.filesDropNote || 'Drop files here or %{browse}';

  const [uppy] = useState(() => createUppy(config, id, !initialState || initialState.length === 0));

  const theme = useTheme();
  const styles = getStyles(theme);

  useEffect(() => {
    if (idToRemove) {
      // handle removing file to be in line with uppy inner state
      uppy.removeFile(idToRemove, 'removed-by-user');
    }
  }, [idToRemove]);

  useEffect(() => {
    if (idToRetry) {
      uppy.retryUpload(idToRetry).then(() => {
        console.log(idToRetry);
      });
    }
  }, [idToRetry]);

  useEffect(() => {
    if (uppy) {
      if (initialState && uppy.getFiles().length === 0) {
        prepareState(initialState, config.restrictionsRules.maxNumberOfFiles || 1).forEach((item) => {
          const fileId = uppy.addFile({
            name: item.name ?? '',
            type: 'image/jpeg',
            data: new Blob(),
            meta: {
              isInitial: true,
              // ID in our DB
              documentId: item.documentId,
              url: item.url,
            },
          });
          uppy.setFileState(fileId, {
            progress: { uploadComplete: true, uploadStarted: true },
            uploadURL: item.url,
          });
        });
        uppy.setOptions({
          autoProceed: true,
        });
      }
    }
  }, [uppy, id, initialState]);

  useEffect(() => {
    if (uppy && rawFilesToUpload && rawFilesToUpload.length > 0) {
      rawFilesToUpload.forEach((item) => {
        uppy.addFile({
          name: 'signature.svg',
          type: item.type,
          data: item,
        });
      });
      uppy.setOptions({
        autoProceed: true,
      });
    }
  }, [uppy, id, rawFilesToUpload]);

  useEffect(() => {
    if (reset && uppy) {
      uppy.getFiles().forEach((file) => {
        uppy.removeFile(file.id);
      });
    }
  }, [reset]);

  return (
    <>
      {uppy && (
        <>
          {config.isWithStatusBar && (
            <Box sx={styles.statusBar}>
              <StatusBar
                id={id}
                uppy={uppy}
                hideAfterFinish={true}
                hideUploadButton={true}
                hideRetryButton={true}
                hidePauseResumeButton={true}
                hideCancelButton={true}
              />
            </Box>
          )}
          <Box display={dragDropVisible ? 'block' : 'none'}>
            <Box sx={styles.root} className={clsx(isDisabled ? 'disabled' : '')}>
              <DragDrop
                id={id}
                uppy={uppy}
                note={config.restrictionsNote}
                locale={{
                  strings: {
                    dropHereOr: filesDropNote,
                    browse: 'browse',
                  },
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
